import type { Estate, Field, Geometry, Position } from '../types';
import { id } from './domain';
import {
  createPoiCircleGeometry,
  formatJagtPoiNotes,
  getFieldCentroid,
  type JagtPoiType,
} from './poi';

export type CameraBrand = 'ucon' | 'spypoint' | 'mailbox' | 'sdcard';

export interface CameraPhoto {
  id: string;
  url: string;
  timestamp: string;
  notes?: string;
  species?: string;
}

export interface TrailCameraIntegration {
  id: string;
  estateId: string;
  name: string;
  brand: CameraBrand;
  modelName?: string;
  fieldId?: string; // Reference til POI på kortet
  coordinates?: [number, number]; // [lat, lng]
  status: 'online' | 'offline' | 'warning';
  batteryPercent: number;
  signalLevel: number; // 1-5
  lastPhotoUrl?: string;
  lastPhotoAt?: string;
  photos?: CameraPhoto[];
  inboundEmail?: string;
  uconShareUrl?: string;
  uconDeviceId?: string;
  spypointUsername?: string;
  notes?: string;
  createdAt: string;
}

export type TrapStatus = 'ok' | 'triggered' | 'low_battery' | 'offline';

export interface TrapSensorIntegration {
  id: string;
  estateId: string;
  name: string;
  brand: 'minkpolice' | 'trapmaster' | 'uovision' | 'other';
  fieldId?: string;
  coordinates?: [number, number]; // [lat, lng]
  status: TrapStatus;
  batteryPercent: number;
  lastHeartbeatAt: string;
  lastTriggeredAt?: string;
  notes?: string;
  createdAt: string;
}

export interface GoogleDriveIntegration {
  connected: boolean;
  email: string;
  quotaBytesTotal: number; // fx 5 TB
  quotaBytesUsed: number;
  autoSyncPhotos: boolean;
  backupFolder: string;
  lastSyncAt?: string;
}

export interface DogGpsIntegration {
  brand: 'garmin' | 'wehunt' | 'tracker';
  lastExportedGpxAt?: string;
  importedTracksCount: number;
}

export interface EstateIntegrationsState {
  estateId: string;
  cameras: TrailCameraIntegration[];
  traps: TrapSensorIntegration[];
  googleDrive: GoogleDriveIntegration;
  dogGps: DogGpsIntegration;
  updatedAt: string;
}

const STORAGE_KEY_PREFIX = 'plotwise.integrations.';

export function getIntegrationsStorageKey(estateId: string): string {
  return `${STORAGE_KEY_PREFIX}${estateId}`;
}

export function createDefaultIntegrationsState(estateId: string): EstateIntegrationsState {
  return {
    estateId,
    cameras: [
      {
        id: `cam-ucon-${estateId.slice(0, 4)}`,
        estateId,
        name: 'UCon 4G Solcelle Vildtkamera (Nordremise)',
        brand: 'ucon',
        modelName: 'UCon 4G LTE Solar 2K',
        uconShareUrl:
          'https://wx.ubianet.com/uboxpro?event=share&share_id=us_94ZgayYbg7mLqQG8RJYrzg&login_node=us',
        uconDeviceId: 'UCON-8849-DK',
        status: 'online',
        batteryPercent: 94,
        signalLevel: 5,
        lastPhotoUrl:
          'https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?auto=format&fit=crop&w=800&q=80',
        lastPhotoAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
        photos: [
          {
            id: 'ph-1',
            url: 'https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?auto=format&fit=crop&w=800&q=80',
            timestamp: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
            notes: 'PIR bevægelse detekteret – Råbuk ved foderplads',
            species: 'Rådyr',
          },
          {
            id: 'ph-2',
            url: 'https://images.unsplash.com/photo-1575550959106-5a7defe28b56?auto=format&fit=crop&w=800&q=80',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
            notes: 'Ræv passerer foderplads i morgengry',
            species: 'Ræv',
          },
          {
            id: 'ph-3',
            url: 'https://images.unsplash.com/photo-1535083783855-76ae62b2914e?auto=format&fit=crop&w=800&q=80',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 22).toISOString(),
            notes: 'Flok fasaner fouragerer ved skovkant',
            species: 'Fasan',
          },
        ],
        notes: 'PIR bevægelse detekteret – Råbuk ved foderplads',
        createdAt: new Date().toISOString(),
      },
      {
        id: `cam-mail-${estateId.slice(0, 4)}`,
        estateId,
        name: 'Bolyguard 4G Mailbox (Skovkanten)',
        brand: 'mailbox',
        modelName: 'Bolyguard SG520 LTE',
        inboundEmail: `cam-${estateId.slice(0, 6)}@cams.plotwise.dk`,
        status: 'online',
        batteryPercent: 78,
        signalLevel: 4,
        lastPhotoUrl:
          'https://images.unsplash.com/photo-1484406566174-9da000fda645?auto=format&fit=crop&w=800&q=80',
        lastPhotoAt: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
        photos: [
          {
            id: 'ph-mail-1',
            url: 'https://images.unsplash.com/photo-1484406566174-9da000fda645?auto=format&fit=crop&w=800&q=80',
            timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
            notes: 'Dåhjort observeret på markvej',
            species: 'Dåvildt',
          },
          {
            id: 'ph-mail-2',
            url: 'https://images.unsplash.com/photo-1547721064-da6cfb341d50?auto=format&fit=crop&w=800&q=80',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 14).toISOString(),
            notes: 'Kronhjort i aftensol ved remisen',
            species: 'Kronvildt',
          },
        ],
        notes: 'Dåhjort observeret på markvej',
        createdAt: new Date().toISOString(),
      },
    ],
    traps: [
      {
        id: `trap-mp1-${estateId.slice(0, 4)}`,
        estateId,
        name: 'MinkPolice #1 (Moseenden)',
        brand: 'minkpolice',
        status: 'ok',
        batteryPercent: 88,
        lastHeartbeatAt: new Date(Date.now() - 1000 * 60 * 75).toISOString(),
        notes: 'Dagligt tilsyn godkendt via automatisk rapport',
        createdAt: new Date().toISOString(),
      },
    ],
    googleDrive: {
      connected: true,
      email: 'mikkelmo2806@gmail.com',
      quotaBytesTotal: 5 * 1024 * 1024 * 1024 * 1024, // 5 TB
      quotaBytesUsed: 42.4 * 1024 * 1024 * 1024, // 42.4 GB
      autoSyncPhotos: true,
      backupFolder: 'Plotwise Pro / Vildtkameraer',
      lastSyncAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    },
    dogGps: {
      brand: 'garmin',
      lastExportedGpxAt: undefined,
      importedTracksCount: 0,
    },
    updatedAt: new Date().toISOString(),
  };
}

const inMemoryStorage = new Map<string, string>();

function getStorageItem(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem) {
      return localStorage.getItem(key);
    }
  } catch {}
  return inMemoryStorage.get(key) || null;
}

function setStorageItem(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.setItem) {
      localStorage.setItem(key, value);
      return;
    }
  } catch {}
  inMemoryStorage.set(key, value);
}

export function loadEstateIntegrations(estateId: string): EstateIntegrationsState {
  try {
    const raw = getStorageItem(getIntegrationsStorageKey(estateId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.estateId === estateId) {
        return parsed;
      }
    }
  } catch {}

  const fallback = createDefaultIntegrationsState(estateId);
  saveEstateIntegrations(estateId, fallback);
  return fallback;
}

export function saveEstateIntegrations(estateId: string, state: EstateIntegrationsState): void {
  try {
    setStorageItem(getIntegrationsStorageKey(estateId), JSON.stringify(state));
  } catch {}
}

/**
 * Fjerner eventuel kobling til en slettet markør (fieldId) fra alle kameraer og fældesensorer.
 */
export function unlinkFieldFromIntegrations(
  estateId: string,
  fieldId: string,
): EstateIntegrationsState {
  const current = loadEstateIntegrations(estateId);
  let changed = false;

  const cameras = current.cameras.map((c) => {
    if (c.fieldId === fieldId) {
      changed = true;
      return { ...c, fieldId: undefined, coordinates: undefined };
    }
    return c;
  });

  const traps = current.traps.map((t) => {
    if (t.fieldId === fieldId) {
      changed = true;
      return { ...t, fieldId: undefined, coordinates: undefined };
    }
    return t;
  });

  if (changed) {
    const next: EstateIntegrationsState = {
      ...current,
      cameras,
      traps,
      updatedAt: new Date().toISOString(),
    };
    saveEstateIntegrations(estateId, next);
    return next;
  }

  return current;
}

/**
 * Opdaterer et vildtkamera (navn, model, brand, noter, status, batteri mv.)
 */
export function updateIntegrationCamera(
  estateId: string,
  updatedCam: TrailCameraIntegration,
): EstateIntegrationsState {
  const current = loadEstateIntegrations(estateId);
  const cameras = current.cameras.map((c) => (c.id === updatedCam.id ? updatedCam : c));
  const next: EstateIntegrationsState = {
    ...current,
    cameras,
    updatedAt: new Date().toISOString(),
  };
  saveEstateIntegrations(estateId, next);
  return next;
}

/**
 * Opdaterer en fældesensor (navn, brand, status, noter mv.)
 */
export function updateIntegrationTrap(
  estateId: string,
  updatedTrap: TrapSensorIntegration,
): EstateIntegrationsState {
  const current = loadEstateIntegrations(estateId);
  const traps = current.traps.map((t) => (t.id === updatedTrap.id ? updatedTrap : t));
  const next: EstateIntegrationsState = {
    ...current,
    traps,
    updatedAt: new Date().toISOString(),
  };
  saveEstateIntegrations(estateId, next);
  return next;
}

/**
 * Tilføjer et billede til et vildtkamera
 */
export function addPhotoToCamera(
  estateId: string,
  camId: string,
  photo: CameraPhoto,
): EstateIntegrationsState {
  const current = loadEstateIntegrations(estateId);
  const cameras = current.cameras.map((c) => {
    if (c.id === camId) {
      const photos = [photo, ...(c.photos || [])];
      return {
        ...c,
        photos,
        lastPhotoUrl: photo.url,
        lastPhotoAt: photo.timestamp,
      };
    }
    return c;
  });
  const next: EstateIntegrationsState = {
    ...current,
    cameras,
    updatedAt: new Date().toISOString(),
  };
  saveEstateIntegrations(estateId, next);
  return next;
}

/**
 * Sletter et billede fra et vildtkamera
 */
export function removePhotoFromCamera(
  estateId: string,
  camId: string,
  photoId: string,
): EstateIntegrationsState {
  const current = loadEstateIntegrations(estateId);
  const cameras = current.cameras.map((c) => {
    if (c.id === camId) {
      const photos = (c.photos || []).filter((p) => p.id !== photoId);
      const lastPhoto = photos[0];
      return {
        ...c,
        photos,
        lastPhotoUrl: lastPhoto ? lastPhoto.url : undefined,
        lastPhotoAt: lastPhoto ? lastPhoto.timestamp : undefined,
      };
    }
    return c;
  });
  const next: EstateIntegrationsState = {
    ...current,
    cameras,
    updatedAt: new Date().toISOString(),
  };
  saveEstateIntegrations(estateId, next);
  return next;
}

export function generateCameraEmail(estateId: string, cameraName: string): string {
  const slug = cameraName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 10);
  return `cam-${slug || 'foto'}-${estateId.slice(0, 4)}@cams.plotwise.dk`;
}

/**
 * Opretter et nyt POI-field på kortet ud fra en integration (kamera eller fælde)
 */
export function createPoiFieldFromIntegration({
  estateId,
  name,
  type,
  coordinates,
  notes,
}: {
  estateId: string;
  name: string;
  type: 'vildtkamera' | 'faeldesensor';
  coordinates: [number, number]; // [lat, lng]
  notes?: string;
}): Field {
  const [lat, lng] = coordinates;
  const geometry = createPoiCircleGeometry(lat, lng, 0.004);
  const poiNotes = formatJagtPoiNotes(type, undefined, notes || name);

  return {
    id: id(),
    estateId,
    name,
    geometry,
    areaType: 'skov',
    notes: poiNotes,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Genererer en standardiseret Garmin GPX XML-fil med alle revirets jagtposter, tårne,
 * vildtkameraer og markgrænser til upload på Garmin Alpha, Atemos eller ure.
 */
export function exportGarminGpx(estate: Estate, fields: Field[]): string {
  const escapeXml = (unsafe: string) =>
    unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

  const estateFields = fields.filter((f) => f.estateId === estate.id);

  let wpts = '';
  for (const field of estateFields) {
    const centroid = getFieldCentroid(field.geometry);
    if (!centroid) continue;

    let sym = 'Hunting Area';
    if (field.notes?.includes('[POI:skydetaarn')) {
      sym = 'Hunting Stand';
    } else if (field.notes?.includes('[POI:vildtkamera')) {
      sym = 'Camera';
    } else if (field.notes?.includes('[POI:fodertoende')) {
      sym = 'Food Source';
    } else if (field.notes?.includes('[POI:faeldesensor')) {
      sym = 'Trap';
    }

    wpts += `  <wpt lat="${centroid.lat.toFixed(6)}" lon="${centroid.lng.toFixed(6)}">
    <name>${escapeXml(field.name)}</name>
    <desc>${escapeXml(field.notes || field.name)}</desc>
    <sym>${sym}</sym>
  </wpt>\n`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Plotwise Pro - https://plotwise.dk"
  xmlns="http://www.topografix.com/GPX/1/1"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(estate.name)} - Jagt &amp; Revirdata</name>
    <desc>Eksporteret fra Plotwise Pro til Garmin GPS og hundehalsbånd</desc>
    <time>${new Date().toISOString()}</time>
  </metadata>
${wpts}</gpx>`;
}
